import React, {
  useState,
  useEffect,
} from "react"

import {
  PlusIcon,
  MinusIcon,
  TrashIcon,
  PencilSquareIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline"

import PulseLoader
  from "react-spinners/PulseLoader"

import EtBtnFormBasic
  from "@/components/common/buttons/EtBtnFormBasic"

import Modal
  from "@/components/common/modal/Modal"

import {
  useGetOrgIntentionCandidatesQuery,
  useCreateNewOrgIntentionCandidateMutation,
  useUpdateOrgIntentionCandidateMutation,
  useDeleteOrgIntentionCandidateMutation,
} from "@/features/orgIntentionBoards/orgIntentionCandidatesApiSlice"


const EtOrgIgniterIntentionCandidateManager = ({
  intentionBoardId,
}) => {

  // First Created - 08 Sep 2026
  // Author - Atul Pandey
  // Purpose - Org Igniter candidate nomination manager for
  //           Trinity Intention Boards.
  //
  // Org Sponsor / Org Facilitator permissions:
  //
  // - View candidates
  // - Add candidate name + email
  // - Update candidate name only
  // - Delete candidate only before any response has been saved
  //
  // Backend remains authoritative for all permission enforcement.

  /*
  |--------------------------------------------------------------------------
  | 01 - State
  |--------------------------------------------------------------------------
  */

  const [
    showManager,
    setShowManager,
  ] =
    useState(
      true
    )

  const [
    editCandidateId,
    setEditCandidateId,
  ] =
    useState(
      null
    )

  const [
    deleteCandidateId,
    setDeleteCandidateId,
  ] =
    useState(
      null
    )

  const [
    isDeleteModalOpen,
    setIsDeleteModalOpen,
  ] =
    useState(
      false
    )

  /*
  |--------------------------------------------------------------------------
  | 02 - Get Candidates
  |--------------------------------------------------------------------------
  */

  const {
    data:
      orgIntentionCandidatesData,

    isLoading:
      isLoadingCandidates,

    isFetching:

      isFetchingCandidates,

    isError:
      isCandidatesError,

    error:
      candidatesError,

  } =
    useGetOrgIntentionCandidatesQuery(
      {
        intentionBoardId,
      },
      {
        skip:
          !intentionBoardId,
      }
    )

  /*
  |--------------------------------------------------------------------------
  | 03 - Mutations
  |--------------------------------------------------------------------------
  */

  const [
    createNewOrgIntentionCandidate,
    {
      isLoading:
        isCreating,

      isSuccess:
        isCreateSuccess,

      isError:
        isCreateError,

      error:
        createError,
    },
  ] =
    useCreateNewOrgIntentionCandidateMutation()

  const [
    updateOrgIntentionCandidate,
    {
      isLoading:
        isUpdating,

      isSuccess:
        isUpdateSuccess,

      isError:
        isUpdateError,

      error:
        updateError,
    },
  ] =
    useUpdateOrgIntentionCandidateMutation()

  const [
    deleteOrgIntentionCandidate,
    {
      isLoading:
        isDeleting,

      isError:
        isDeleteError,

      error:
        deleteError,
    },
  ] =
    useDeleteOrgIntentionCandidateMutation()

  /*
  |--------------------------------------------------------------------------
  | 04 - Normalize Candidates
  |--------------------------------------------------------------------------
  */

  const orgIntentionCandidates =
    orgIntentionCandidatesData
      ?.ids
      ?.map(
        id =>
          orgIntentionCandidatesData
            ?.entities[id]
      )
      ?.sort(
        (
          a,
          b
        ) =>
          (
            a
              ?.candidateName ||
            ""
          )
            .localeCompare(
              b
                ?.candidateName ||
              ""
            )
      ) ||
    []

  /*
  |--------------------------------------------------------------------------
  | 05 - Initial Form State
  |--------------------------------------------------------------------------
  */

  const initialFormState = {

    candidateName:
      "",

    candidateEmail:
      "",

  }

  const [
    formData,
    setFormData,
  ] =
    useState(
      initialFormState
    )

  /*
  |--------------------------------------------------------------------------
  | 06 - Reset After Save
  |--------------------------------------------------------------------------
  */

  useEffect(
    () => {

      if (
        isCreateSuccess ||
        isUpdateSuccess
      ) {

        setEditCandidateId(
          null
        )

        setFormData(
          initialFormState
        )

      }

    },
    [
      isCreateSuccess,
      isUpdateSuccess,
    ]
  )

  /*
  |--------------------------------------------------------------------------
  | 07 - Handle Change
  |--------------------------------------------------------------------------
  */

  const handleChange =
    e => {

      const {
        name,
        value,
      } =
        e.target

      setFormData(
        prev => ({

          ...prev,

          [name]:
            value,

        })
      )

    }

  /*
  |--------------------------------------------------------------------------
  | 08 - Edit Candidate
  |--------------------------------------------------------------------------
  |
  | Org Igniters may update candidate NAME only.
  |
  | Email is shown for context but is not editable.
  |
  */

  const handleEditCandidate =
    candidate => {

      setEditCandidateId(
        candidate
          ?._id
      )

      setFormData({

        candidateName:
          candidate
            ?.candidateName ||
          "",

        candidateEmail:
          candidate
            ?.candidateEmail ||
          "",

      })

    }

  /*
  |--------------------------------------------------------------------------
  | 09 - Cancel Edit
  |--------------------------------------------------------------------------
  */

  const handleCancelEdit =
    () => {

      setEditCandidateId(
        null
      )

      setFormData(
        initialFormState
      )

    }

  /*
  |--------------------------------------------------------------------------
  | 10 - Submit Candidate
  |--------------------------------------------------------------------------
  */

  const handleSubmit =
    async e => {

      e.preventDefault()

      try {

        /*
        |--------------------------------------------------------------------------
        | Update Existing Candidate
        |--------------------------------------------------------------------------
        |
        | IMPORTANT:
        |
        | For Org Sponsor / Org Facilitator, send candidateName only.
        |
        | Do not send candidateEmail, isActive, lifecycle or decision fields.
        |
        */

        if (
          editCandidateId
        ) {

          await updateOrgIntentionCandidate({

            intentionCandidateId:
              editCandidateId,

            intentionBoardId,

            candidateName:
              formData
                .candidateName,

          })
            .unwrap()

        } else {

          /*
          |--------------------------------------------------------------------------
          | Create Candidate
          |--------------------------------------------------------------------------
          */

          await createNewOrgIntentionCandidate({

            intentionBoardId,

            candidateName:
              formData
                .candidateName,

            candidateEmail:
              formData
                .candidateEmail,

          })
            .unwrap()

        }

      } catch (
        error
      ) {

        console.error(
          error
        )

      }

    }

  /*
  |--------------------------------------------------------------------------
  | 11 - Delete Candidate
  |--------------------------------------------------------------------------
  */

  const handleOpenDeleteModal =
    intentionCandidateId => {

      setDeleteCandidateId(
        intentionCandidateId
      )

      setIsDeleteModalOpen(
        true
      )

    }

  const handleCloseDeleteModal =
    () => {

      setDeleteCandidateId(
        null
      )

      setIsDeleteModalOpen(
        false
      )

    }

  const handleDeleteCandidate =
    async e => {

      e.preventDefault()

      if (
        !deleteCandidateId
      ) {

        return

      }

      try {

        await deleteOrgIntentionCandidate({

          intentionCandidateId:
            deleteCandidateId,

          intentionBoardId,

        })
          .unwrap()

      } catch (
        error
      ) {

        console.error(
          error
        )

      } finally {

        handleCloseDeleteModal()

      }

    }

  /*
  |--------------------------------------------------------------------------
  | 12 - Candidate Status
  |--------------------------------------------------------------------------
  */

  const getCandidateStatusClass =
    candidateStatus => {

      switch (
        candidateStatus
      ) {

        case "submitted":
          return "bg-lime-100 text-lime-700"

        case "draft":
          return "bg-yellow-100 text-yellow-700"

        case "accessed":
          return "bg-blue-100 text-blue-700"

        case "invited":
          return "bg-orange-100 text-orange-700"

        case "nominated":
          return "bg-gray-100 text-gray-700"

        case "expired":
          return "bg-red-100 text-red-700"

        case "withdrawn":
          return "bg-gray-200 text-gray-500"

        default:
          return "bg-gray-100 text-gray-600"

      }

    }

  /*
  |--------------------------------------------------------------------------
  | 13 - Format Date
  |--------------------------------------------------------------------------
  */

  const formatDate =
    dateValue => {

      if (
        !dateValue
      ) {

        return "Not Available"

      }

      return new Date(
        dateValue
      )
        .toLocaleString()

    }

  /*
  |--------------------------------------------------------------------------
  | 14 - Delete Availability
  |--------------------------------------------------------------------------
  |
  | Frontend provides a conservative UX.
  |
  | Backend remains authoritative and will return 409 if ANY saved
  | response exists.
  |
  | Candidate statuses accessed / draft / submitted indicate the candidate
  | has already interacted with the board and therefore we do not display
  | the delete action.
  |
  */

  const canDeleteCandidate =
    candidate => {

      return (
        candidate
          ?.candidateStatus ===
          "nominated" ||
        candidate
          ?.candidateStatus ===
          "invited"
      )

    }

  /*
  |--------------------------------------------------------------------------
  | 15 - Loading / Errors
  |--------------------------------------------------------------------------
  */

  const isLoading =
    isLoadingCandidates ||
    isCreating ||
    isUpdating ||
    isDeleting

  const mutationError =
    createError ||
    updateError ||
    deleteError

  const mutationIsError =
    isCreateError ||
    isUpdateError ||
    isDeleteError

  /*
  |--------------------------------------------------------------------------
  | 16 - Render
  |--------------------------------------------------------------------------
  */

  return (

    <div className="m-1 p-1 bg-white rounded">

      {/*
      |--------------------------------------------------------------------------
      | Header
      |--------------------------------------------------------------------------
      */}

      <div className="flex justify-between items-center m-1 p-1">

        <div className="flex justify-start items-center">

          <div>

            <p className="font-bold text-lg text-gray-800">
              Intention Candidates
            </p>

            <p className="text-[10px] text-gray-500 mt-1">
              Nominate and manage candidates for this Intention Board.
            </p>

          </div>

          <div
            onClick={
              () =>
                setShowManager(
                  prev =>
                    !prev
                )
            }
            className="m-1 ml-2 p-1 cursor-pointer rounded-full h-7 w-7 flex justify-center items-center hover:bg-gray-100"
          >

            {showManager ? (

              <MinusIcon className="h-5 w-5 text-orange-600" />

            ) : (

              <PlusIcon className="h-4 w-4 text-orange-600" />

            )}

          </div>

        </div>

        {isFetchingCandidates &&
          !isLoadingCandidates && (

          <PulseLoader
            color={"#ea580c"}
            size={4}
          />

        )}

      </div>

      {showManager && (

        <div>

          {/*
          |--------------------------------------------------------------------------
          | Loading
          |--------------------------------------------------------------------------
          */}

          {isLoading && (

            <div className="m-1 p-2">

              <PulseLoader
                color={"#ea580c"}
                size={6}
              />

            </div>

          )}

          {/*
          |--------------------------------------------------------------------------
          | Query Error
          |--------------------------------------------------------------------------
          */}

          {isCandidatesError && (

            <p className="text-orange-700 text-xs p-2">

              {
                candidatesError
                  ?.data
                  ?.message ||
                "Unable to load Intention Board candidates"
              }

            </p>

          )}

          {/*
          |--------------------------------------------------------------------------
          | Mutation Error
          |--------------------------------------------------------------------------
          */}

          {mutationIsError && (

            <div className="bg-orange-50 rounded m-1 p-2">

              <p className="text-orange-700 text-xs">

                {
                  mutationError
                    ?.data
                    ?.message ||
                  "Unable to manage Intention Board candidate"
                }

              </p>

            </div>

          )}

          {/*
          |--------------------------------------------------------------------------
          | Existing Candidates
          |--------------------------------------------------------------------------
          */}

          <div className="bg-gray-200 rounded m-1 p-1">

            <p className="font-bold text-xs m-2">
              Nominated Candidates
            </p>

            {orgIntentionCandidates
              ?.length ===
              0 && (

              <div className="bg-white rounded m-1 p-2">

                <p className="text-xs text-gray-500">
                  No candidates have been nominated for this Intention Board.
                </p>

              </div>

            )}

            {orgIntentionCandidates
              ?.map(
                (
                  candidate,
                  index
                ) => (

                  <div
                    key={
                      candidate
                        ?._id
                    }
                    className="bg-white rounded m-1 p-2"
                  >

                    <div className="flex justify-between items-start">

                      <div className="flex-1">

                        {/*
                        |--------------------------------------------------------------------------
                        | Candidate Header
                        |--------------------------------------------------------------------------
                        */}

                        <div className="flex justify-start items-center flex-wrap">

                          <p className="font-bold text-xs">

                            {
                              index +
                              1
                            }
                            .{" "}
                            {
                              candidate
                                ?.candidateName
                            }

                          </p>

                          <p
                            className={`text-[9px] rounded-full mx-2 px-2 py-1 ${
                              getCandidateStatusClass(
                                candidate
                                  ?.candidateStatus
                              )
                            }`}
                          >

                            {
                              candidate
                                ?.candidateStatus ||
                              "nominated"
                            }

                          </p>

                        </div>

                        {/*
                        |--------------------------------------------------------------------------
                        | Email
                        |--------------------------------------------------------------------------
                        */}

                        <p className="text-[10px] text-gray-600 mt-1">

                          {
                            candidate
                              ?.candidateEmail
                          }

                        </p>

                        {/*
                        |--------------------------------------------------------------------------
                        | Lifecycle Dates
                        |--------------------------------------------------------------------------
                        */}

                        <div className="flex justify-start items-center flex-wrap mt-1">

                          <p className="text-[9px] text-gray-500 mr-3">

                            Nominated: {
                              formatDate(
                                candidate
                                  ?.nominatedAt
                              )
                            }

                          </p>

                          {candidate
                            ?.invitedAt && (

                            <p className="text-[9px] text-gray-500 mr-3">

                              Invited: {
                                formatDate(
                                  candidate
                                    ?.invitedAt
                                )
                              }

                            </p>

                          )}

                          {candidate
                            ?.firstAccessedAt && (

                            <p className="text-[9px] text-gray-500 mr-3">

                              First Access: {
                                formatDate(
                                  candidate
                                    ?.firstAccessedAt
                                )
                              }

                            </p>

                          )}

                          {candidate
                            ?.submittedAt && (

                            <p className="text-[9px] text-gray-500">

                              Submitted: {
                                formatDate(
                                  candidate
                                    ?.submittedAt
                                )
                              }

                            </p>

                          )}

                        </div>

                      </div>

                      {/*
                      |--------------------------------------------------------------------------
                      | Actions
                      |--------------------------------------------------------------------------
                      */}

                      <div className="flex justify-start items-center">

                        {/*
                        |--------------------------------------------------------------------------
                        | Rename Candidate
                        |--------------------------------------------------------------------------
                        */}

                        <div className="rounded-full flex justify-center items-center h-6 w-6 hover:bg-gray-100">

                          <PencilSquareIcon
                            onClick={
                              () =>
                                handleEditCandidate(
                                  candidate
                                )
                            }
                            className="h-4 w-4 text-orange-600 cursor-pointer"
                            title="Update candidate name"
                          />

                        </div>

                        {/*
                        |--------------------------------------------------------------------------
                        | Delete Candidate
                        |--------------------------------------------------------------------------
                        */}

                        {canDeleteCandidate(
                          candidate
                        ) && (

                          <div className="rounded-full flex justify-center items-center h-6 w-6 hover:bg-gray-100">

                            <TrashIcon
                              onClick={
                                () =>
                                  handleOpenDeleteModal(
                                    candidate
                                      ?._id
                                  )
                              }
                              className="h-4 w-4 text-orange-600 cursor-pointer"
                              title="Remove candidate"
                            />

                          </div>

                        )}

                      </div>

                    </div>

                  </div>

                )
              )}

          </div>

          {/*
          |--------------------------------------------------------------------------
          | Add / Rename Candidate
          |--------------------------------------------------------------------------
          */}

          <div className="bg-gray-200 rounded m-1 p-1">

            <div className="flex justify-between items-center m-1 p-1">

              <div>

                <p className="font-bold text-xs">

                  {
                    editCandidateId
                      ? "Update Candidate Name"
                      : "Nominate Candidate"
                  }

                </p>

                {editCandidateId && (

                  <p className="text-[9px] text-gray-500 mt-1">
                    Candidate email cannot be changed after nomination.
                  </p>

                )}

              </div>

              {editCandidateId && (

                <XMarkIcon
                  onClick={
                    handleCancelEdit
                  }
                  className="h-5 w-5 text-orange-600 cursor-pointer"
                />

              )}

            </div>

            <form
              onSubmit={
                handleSubmit
              }
              className="m-1 p-1 text-xs font-poppins"
            >

              {/*
              |--------------------------------------------------------------------------
              | Candidate Name
              |--------------------------------------------------------------------------
              */}

              <input
                type="text"
                name="candidateName"
                placeholder="Candidate name"
                value={
                  formData
                    .candidateName
                }
                onChange={
                  handleChange
                }
                className="w-full rounded p-2 m-1"
              />

              {/*
              |--------------------------------------------------------------------------
              | Candidate Email
              |--------------------------------------------------------------------------
              |
              | Email is required during nomination.
              |
              | Once nominated it becomes read only for Org Igniters.
              |
              */}

              <input
                type="email"
                name="candidateEmail"
                placeholder="Candidate email"
                value={
                  formData
                    .candidateEmail
                }
                onChange={
                  handleChange
                }
                disabled={
                  Boolean(
                    editCandidateId
                  )
                }
                className={`w-full rounded p-2 m-1 ${
                  editCandidateId
                    ? "bg-gray-100 text-gray-500 cursor-not-allowed"
                    : ""
                }`}
              />

              {/*
              |--------------------------------------------------------------------------
              | Actions
              |--------------------------------------------------------------------------
              */}

              <div
                id="actions"
                className="bg-white m-1 px-1 py-3 rounded"
              >

                <EtBtnFormBasic
                  name={
                    editCandidateId
                      ? "Update Name"
                      : "Add Candidate"
                  }
                  type={"create"}
                  handleClick={
                    handleSubmit
                  }
                  disabled={
                    isCreating ||
                    isUpdating ||
                    !formData
                      .candidateName
                      ?.trim() ||
                    (
                      !editCandidateId &&
                      !formData
                        .candidateEmail
                        ?.trim()
                    )
                  }
                />

                {editCandidateId && (

                  <EtBtnFormBasic
                    name={"Cancel Edit"}
                    type={"cancel"}
                    handleClick={
                      handleCancelEdit
                    }
                  />

                )}

              </div>

            </form>

          </div>

        </div>

      )}

      {/*
      |--------------------------------------------------------------------------
      | Delete Confirmation
      |--------------------------------------------------------------------------
      */}

      <Modal
        isOpen={
          isDeleteModalOpen
        }
        onClose={
          handleCloseDeleteModal
        }
        onConfirm={
          handleDeleteCandidate
        }
      />

    </div>

  )

}

export default EtOrgIgniterIntentionCandidateManager